import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CartSidebar from "./components/CartSidebar";
import ExitIntentPopup from "./components/ExitIntentPopup";
import CookieConsent from "./components/CookieConsent";
import AnalyticsProvider from "./components/AnalyticsProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import TawkChat from "./components/TawkChat";
import { Toaster } from "./components/ui/sonner";
import { initPostHog } from "./lib/posthog";

// -- Eager (critical shopping funnel — must render fast, no Suspense delay) --
import Home from "./pages/Home";
import ShopPage from "./pages/ShopPage";
import CategoryPage from "./pages/CategoryPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import NotFoundPage from "./pages/NotFoundPage";

// -- Lazy (secondary storefront pages — loaded on demand) --
const BundleBuilderPage    = lazy(() => import("./pages/BundleBuilderPage"));
const AboutPage            = lazy(() => import("./pages/AboutPage"));
const WishlistPage         = lazy(() => import("./pages/WishlistPage"));
const HelpPage             = lazy(() => import("./pages/HelpPage"));
const ContactPage          = lazy(() => import("./pages/ContactPage"));
const OrdersPage           = lazy(() => import("./pages/OrdersPage"));
const TrackOrderPage       = lazy(() => import("./pages/TrackOrderPage"));
const Page                 = lazy(() => import("./pages/Page"));
const ChallengesPage       = lazy(() => import("./pages/ChallengesPage"));
const ChallengeDetailPage  = lazy(() => import("./pages/ChallengeDetailPage"));
const FreeResourcesPage    = lazy(() => import("./pages/FreeResourcesPage"));

// -- Lazy (admin pages — never loaded for storefront visitors) --
const AdminLogin           = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard       = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts        = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductForm     = lazy(() => import("./pages/admin/AdminProductForm"));
const AdminCategories      = lazy(() => import("./pages/admin/AdminCategories"));
const AdminPartners        = lazy(() => import("./pages/admin/AdminPartners"));
const AdminSettings        = lazy(() => import("./pages/admin/AdminSettings"));
const AdminPages           = lazy(() => import("./pages/admin/AdminPages"));
const AdminUsers           = lazy(() => import("./pages/admin/AdminUsers"));
const AdminNewsletter      = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminOrders          = lazy(() => import("./pages/admin/AdminOrders"));
const AdminDiscounts       = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminActivityLogs    = lazy(() => import("./pages/admin/AdminActivityLogs"));
const AdminReviews         = lazy(() => import("./pages/admin/AdminReviews"));
const AdminInvitations     = lazy(() => import("./pages/admin/AdminInvitations"));
const AdminActivate        = lazy(() => import("./pages/admin/AdminActivate"));
const AdminProductContent  = lazy(() => import("./pages/admin/AdminProductContent"));
const AdminHomepage        = lazy(() => import("./pages/admin/AdminHomepage"));

// Small suspense fallback — light so it doesn't add CLS
const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center" aria-hidden="true">
    <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-teal-600 animate-spin" />
  </div>
);

initPostHog();

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsProvider>
      <ErrorBoundary>
      <AdminAuthProvider>
        <WishlistProvider>
          <CartProvider>
            <div className="App">
              <Suspense fallback={<RouteFallback />}>
              <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/products/new" element={<AdminProductForm />} />
              <Route path="/admin/products/edit/:id" element={<AdminProductForm />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/partners" element={<AdminPartners />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/pages" element={<AdminPages />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/invitations" element={<AdminInvitations />} />
              <Route path="/admin/products/:slug/content" element={<AdminProductContent />} />
              <Route path="/admin/homepage" element={<AdminHomepage />} />
              <Route path="/admin/activate/:token" element={<AdminActivate />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/discounts" element={<AdminDiscounts />} />
              <Route path="/admin/activity-logs" element={<AdminActivityLogs />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />

              {/* Store Routes */}
              <Route path="/*" element={
                <>
                  <Navbar />
                  <CartSidebar />
                  <ExitIntentPopup />
                  <CookieConsent />
                  <TawkChat />
                  <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/shop" element={<ShopPage />} />
                    <Route path="/category/:slug" element={<CategoryPage />} />
                    <Route path="/shop/:category/:slug" element={<ProductDetailPage />} />
                    {/* Legacy redirects — keep any live external URL (emails, ads, social) working */}
                    <Route path="/product/the-big-feelings-toolkit" element={<Navigate to="/product/emotional-regulation-toolkit" replace />} />
                    <Route path="/product/tantrum-meltdown-management-guide" element={<Navigate to="/product/tantrum-meltdown-control-guide" replace />} />
                    <Route path="/product/tech-free-family-time-guide" element={<Navigate to="/product/reclaim-family-time-from-screens" replace />} />
                    <Route path="/product/family-connection-toolkit" element={<Navigate to="/product/stronger-family-connection-toolkit" replace />} />
                    <Route path="/product/growth-mindset-resilience-guide" element={<Navigate to="/product/focus-listening-skills-builder" replace />} />
                    <Route path="/product/:slug" element={<ProductDetailPage />} />
                    <Route path="/challenges" element={<ChallengesPage />} />
                    <Route path="/challenges/:slug" element={<ChallengeDetailPage />} />
                    <Route path="/free-resources" element={<FreeResourcesPage />} />
                    <Route path="/bundle-builder" element={<BundleBuilderPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/order-success" element={<OrderSuccessPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/wishlist" element={<WishlistPage />} />
                    <Route path="/help" element={<HelpPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/orders" element={<OrdersPage />} />
                    <Route path="/track-order" element={<TrackOrderPage />} />
                    <Route path="/page/:slug" element={<Page />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>
                  <Footer />
                </>
              } />
            </Routes>
            </Suspense>
            <Toaster />
          </div>
        </CartProvider>
        </WishlistProvider>
      </AdminAuthProvider>
      </ErrorBoundary>
      </AnalyticsProvider>
    </BrowserRouter>
  );
}

export default App;
